import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play } from "lucide-react";

const WORKS = [
  { src: "/lavori/f1.jpeg", label: "Pulizia e Cura Aree Verdi", tag: "VERDE" },
  { src: "/lavori/f2.webp", label: "Progettazione Aree Verdi", tag: "PROGETTAZIONE" },
  { src: "/lavori/f3.webp", label: "Verde Condominiale", tag: "CONDOMINI" },
  { src: "/lavori/f4.webp", label: "Manutenzione Giardini", tag: "MANUTENZIONE" },
  { src: "/lavori/f5.webp", label: "Giardini Privati", tag: "PRIVATI" },
  { src: "/lavori/f6.webp", label: "Sgombero e Pulizia Aree Verdi", tag: "SGOMBERO" },
  { src: "/lavori/f7.webp", label: "Cura del Verde Privato", tag: "GIARDINI" },
  { src: "/lavori/f8.webp", label: "Pulizia Terreni Incolti", tag: "TERRENI" },
  { src: "/lavori/f9.jpeg", label: "Ripristino Vialetti e Viali", tag: "RIPRISTINO" },
  { src: "/lavori/f10.webp", label: "Nuove Piantumazioni", tag: "PIANTUMAZIONE" },
  {
    src: "https://plus.unsplash.com/premium_photo-1764004516865-d9cb579c2903?fm=jpg&q=60&w=1200&auto=format&fit=crop",
    label: "Pulizie Interni ed Esterni",
    tag: "PULIZIE",
  },
  {
    src: "https://images.unsplash.com/photo-1686824806174-e9bf6ebadfc5?fm=jpg&q=60&w=1200&auto=format&fit=crop",
    label: "Traslochi e Sgomberi",
    tag: "LOGISTICA",
  },
];

const SLIDES = WORKS.slice(0, 10);

export default function Gallery() {
  const [idx, setIdx] = useState(0);
  const [loaded, setLoaded] = useState({});

  useEffect(() => {
    SLIDES.forEach((s) => {
      const img = new Image();
      img.src = s.src;
    });
  }, []);

  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % SLIDES.length), 3200);
    return () => clearInterval(id);
  }, []);

  return (
    <section id="lavori" data-testid="gallery-section" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="font-mono text-xs uppercase tracking-[0.25em] text-neon"
        >
          {"// Foto dei Lavori"}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="mt-4 max-w-2xl font-display text-2xl font-bold leading-tight tracking-tight sm:text-3xl lg:text-4xl"
        >
          I nostri interventi, <span className="text-outline-cyan">in immagini.</span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-4 max-w-2xl text-sm text-slate-600"
        >
          Le prime 10 foto sono interventi reali HELPLY sul territorio di Lecce e provincia.
        </motion.p>

        <motion.div
          data-testid="gallery-slideshow"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative mt-10 overflow-hidden rounded-2xl border border-[#D9E4F5] shadow-[0_24px_70px_rgba(22,52,95,0.15)]"
        >
          <div className="relative aspect-[4/3] w-full overflow-hidden bg-white sm:aspect-[16/9]">
            <AnimatePresence>
              <motion.img
                key={idx}
                src={SLIDES[idx].src}
                alt={SLIDES[idx].label}
                onLoad={() => setLoaded((l) => (l[idx] ? l : { ...l, [idx]: true }))}
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: loaded[idx] ? 1 : 0, scale: 1.12 }}
                exit={{ opacity: 0 }}
                transition={{
                  opacity: { duration: 0.55, ease: "easeOut" },
                  scale: { duration: 3.2, ease: "linear" },
                }}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </AnimatePresence>
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(22,52,95,0) 55%, rgba(22,52,95,0.75) 100%)",
              }}
            />
            <div className="absolute left-4 top-4 flex items-center gap-2 rounded-full bg-white/85 px-4 py-1.5 backdrop-blur-sm sm:left-5 sm:top-5">
              <Play className="h-3.5 w-3.5 text-pulse" />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#16345F]">
                I lavori in automatico
              </span>
            </div>
            <div
              data-testid="gallery-slideshow-counter"
              className="absolute right-4 top-4 rounded-full bg-[#16345F]/70 px-3 py-1 font-mono text-xs text-white sm:right-5 sm:top-5"
            >
              {String(idx + 1).padStart(2, "0")} / {SLIDES.length}
            </div>
            <div className="absolute bottom-4 left-4 sm:bottom-5 sm:left-5">
              <p className="font-display text-lg font-bold text-white sm:text-2xl">
                {SLIDES[idx].label}
              </p>
            </div>
            <div className="absolute bottom-5 right-5 hidden gap-1.5 sm:flex">
              {SLIDES.map((s, i) => (
                <button
                  key={s.src}
                  data-testid={`gallery-dot-${i}`}
                  onClick={() => setIdx(i)}
                  aria-label={`Vai alla foto ${i + 1}`}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === idx ? "w-6 bg-pulse" : "w-2 bg-white/50 hover:bg-white"
                  }`}
                />
              ))}
            </div>
          </div>
        </motion.div>

        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {WORKS.map((w, i) => (
            <motion.figure
              key={w.label}
              data-testid={`gallery-item-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, delay: (i % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="group relative overflow-hidden rounded-2xl border border-[#D9E4F5] bg-white shadow-[0_8px_32px_rgba(22,52,95,0.08)]"
            >
              <img
                src={w.src}
                alt={w.label}
                loading="lazy"
                className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div
                aria-hidden
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(22,52,95,0) 45%, rgba(22,52,95,0.85) 100%)",
                }}
              />
              <figcaption className="absolute inset-x-0 bottom-0 flex flex-col items-start gap-2 p-4">
                <span className="rounded-full border border-white/30 bg-white/15 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white backdrop-blur-sm">
                  {w.tag}
                </span>
                <span className="font-display text-sm font-semibold text-white sm:text-base">
                  {w.label}
                </span>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}
